// routers/index.js
import { createRouter, createWebHistory } from "vue-router";
import { guideRoutes } from "./guide";
import { aboutRoutes } from "./about";
import { brandRoutes } from "./brand";
import { newsRoutes } from "./news";
import { investorsRoutes } from "./investors";
import { sustainabilityRoutes } from "./sustainability";
import { shinhancardRoutes } from "./shinhancard";
import { startupRoutes } from "./startup";
import { instoreRoutes } from "./instore";
import { inquiryRoutes } from "./inquiry";
import { policyRoutes } from "./policy";
import workListData from "@/assets/language/menu/workList.json";
import { ERROR_ROUTE_PATH, buildErrorLocation } from "@/utils/errorHandler.js";

const mainWrap = () => import("@/views/main_wrap.vue");
const commonErrorPage = () => import("@/components/CommonErrorPage.vue");

const isCatchAllRoute = (route) => route.path && route.path.includes(":pathMatch");
const guideCatchAllRoutes = guideRoutes.filter(isCatchAllRoute);
const guidePageRoutes = guideRoutes.filter((route) => !isCatchAllRoute(route));
const UI_PUB_PREFIX = "/uiPup";

const getUiPubRoutePath = (item) => {
    const itemPath = item.link || `/${item.path}`;
    const normalizedPath = itemPath === "/" ? "" : itemPath;

    return `${UI_PUB_PREFIX}${normalizedPath}`;
};

const getFileName = (path) => {
    return String(path).split("/").filter(Boolean).pop();
};

const isPopupItem = (item) => {
    return String(item.type || "").toLowerCase() === "pop-up";
};

const loadUiPubPage = (item) => {
    if (isPopupItem(item)) {
        return () => import(`@/uiPub/popUp/${getFileName(item.path)}.vue`);
    }

    return () => import(`@/uiPub/${item.path}.vue`);
};

// workList.json에서 path 있는 항목만 라우트로 생성
const workListRoutes = workListData
    .filter((item) => item.path)
    .map((item) => {
        const parentComponent = item.path.includes("gsr") ? "main_wrap" : "sub_wrap";

        return {
            path: getUiPubRoutePath(item),
            component: () => import(`@/uiPub/${parentComponent}.vue`),
            children: [
                {
                    path: "",
                    component: loadUiPubPage(item)
                }
            ]
        };
    });

const mainRoutes = [
    {
        path: "/",
        redirect: "/main"
    },
    {
        path: "/main",
        name: "Main",
        alias: ["/gsrmain"],
        component: mainWrap,
        children: [
            {
                path: "",
                component: () => import("@/views/main/gsrmain.vue")
            }
        ]
    },
    {
        path: "/main/notices",
        name: "GsrNotice",
        alias: ["/gsrnotice", "/main/gsrnotice"],
        component: mainWrap,
        children: [
            {
                path: "",
                component: () => import("@/views/main/gsrnotice.vue")
            }
        ]
    },
    {
        path: "/main/notices/detail",
        name: "GsrNoticeDetail",
        alias: ["/gsrnotice/detail", "/main/gsrnotice/detail"],
        component: mainWrap,
        meta: {
            queryRules: {
                noticeNo: { required: true, pattern: /^\d+$/ }
            }
        },
        children: [
            {
                path: "",
                component: () => import("@/views/main/gsrnotice_detail.vue")
            }
        ]
    }
];

const errorRoutes = [
    {
        path: ERROR_ROUTE_PATH,
        name: "CommonError",
        component: commonErrorPage,
        meta: {
            allowAnyQuery: true
        }
    }
];

const getQueryRules = (route) => {
    return route.matched.reduce((rules, matched) => {
        if (!matched.meta?.queryRules) return rules;

        return {
            ...rules,
            ...matched.meta.queryRules
        };
    }, {});
};

const hasQueryValue = (value) => {
    if (Array.isArray(value)) return value.length > 0 && value.every(hasQueryValue);

    return value !== undefined && value !== null && String(value).trim() !== "";
};

const isValidQueryValue = (value, rule) => {
    if (!hasQueryValue(value)) return !rule.required;
    if (Array.isArray(value)) return value.every((item) => isValidQueryValue(item, rule));
    if (rule.pattern && !rule.pattern.test(String(value))) return false;
    if (rule.values && !rule.values.includes(String(value))) return false;

    return true;
};

const isValidRouteQuery = (route) => {
    if (route.matched.some((matched) => matched.meta?.allowAnyQuery)) return true;

    const queryKeys = Object.keys(route.query || {});
    const queryRules = getQueryRules(route);
    const allowedKeys = Object.keys(queryRules);

    if (!allowedKeys.length) return true;

    if (queryKeys.some((key) => !allowedKeys.includes(key))) return false;
    if (queryKeys.some((key) => !hasQueryValue(route.query[key]))) return false;

    return allowedKeys.every((key) => isValidQueryValue(route.query[key], queryRules[key]));
};

const routes = [
    ...errorRoutes,
    ...mainRoutes,
    ...aboutRoutes,
    ...brandRoutes,
    ...newsRoutes,
    ...investorsRoutes,
    ...sustainabilityRoutes,
    ...shinhancardRoutes,
    ...startupRoutes,
    ...instoreRoutes,
    ...inquiryRoutes,
    ...policyRoutes,
    ...workListRoutes,
    ...guidePageRoutes,
    ...guideCatchAllRoutes.map((route) => ({
        ...route,
        component: commonErrorPage,
        meta: {
            ...(route.meta || {}),
            allowAnyQuery: true
        }
    }))
];

const isBrandOverviewNavigation = (to, from) => {
    return to.path === "/brand/overview" && from.path.startsWith("/brand/") && to.path !== from.path;
};

const TAB_QUERY_KEYS = ["tab", "subTab"];

const normalizeQueryValue = (value) => {
    if (Array.isArray(value)) return value.join(",");

    return value ?? "";
};

const isTabQueryNavigation = (to, from) => {
    if (to.path !== from.path) return false;

    const queryKeys = new Set([...Object.keys(to.query || {}), ...Object.keys(from.query || {})]);
    const changedKeys = [...queryKeys].filter((key) => normalizeQueryValue(to.query[key]) !== normalizeQueryValue(from.query[key]));

    return changedKeys.length > 0 && changedKeys.every((key) => TAB_QUERY_KEYS.includes(key));
};

export const router = createRouter({
    history: createWebHistory(import.meta.env.BASE_URL),
    // history: createWebHistory(process.env.BASE_URL),
    routes,
    scrollBehavior(to, from, savedPosition) {
        if (savedPosition) return savedPosition;
        if (isTabQueryNavigation(to, from)) return false;
        if (to.hash) {
            return {
                el: to.hash,
                behavior: "smooth"
            };
        }
        if (isBrandOverviewNavigation(to, from)) return { top: 0 };

        return { top: 0 };
    }
});

router.beforeEach((to) => {
    if (!isValidRouteQuery(to)) {
        return buildErrorLocation({ code: "400", reason: "invalid-query" });
    }

    return true;
});
